import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent, MouseEvent } from 'react';
import { SubtotalSection } from './SubtotalSection';
import { DiscountSection } from './DiscountSection';
import { BillingFrequencySection } from './BillingFrequencySection';
import { getDiscountValidationErrorKey } from './helpers';
import type { DiscountInput, DiscountType } from './types';
import type { BillingFrequency } from '@proposify/pricing-engine';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { BILLING_FREQUENCIES } from '@proposify/pricing-engine';
import { discountPanelTestIds } from './DiscountPanel.test-ids';

export interface DiscountPanelProps {
  subtotal: string;
  discount: DiscountInput | null;
  onChange: (discount: DiscountInput | null) => void;
  currencySymbol?: string;
  isSubtotalEditable?: boolean;
  onSubtotalChange?: (value: string) => void;
  showBillingFrequency?: boolean;
  billingFrequency?: BillingFrequency;
  onBillingFrequencyChange?: (value: BillingFrequency) => void;
  enabledBillingFrequencies?: BillingFrequency[];
}

export const PanelContainerStyled = styled(Box)(({ theme }) => ({
  padding: '20px 16px',
  maxWidth: 268,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
}));

function isPositiveAmount(raw: string): boolean {
  return raw !== '' && parseFloat(raw) > 0;
}

export const DiscountPanel = ({
  subtotal,
  discount,
  onChange,
  currencySymbol = '$',
  isSubtotalEditable = false,
  onSubtotalChange,
  showBillingFrequency = false,
  billingFrequency,
  onBillingFrequencyChange,
  enabledBillingFrequencies,
}: DiscountPanelProps) => {
  const [enabled, setEnabled] = useState<boolean>(true);
  const [type, setType] = useState<DiscountType>(discount?.type ?? 'percentage');
  const [amount, setAmount] = useState<string>(discount?.value ?? '');
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const lastDiscountRef = useRef<DiscountInput | null>(discount);
  const amountInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (discount) {
      setEnabled(true);
      setType(discount.type);
      setAmount(discount.value);
      lastDiscountRef.current = discount;
    }
  }, [discount]);

  useEffect(() => {
    if (enabled) {
      amountInputRef.current?.focus();
    }
  }, []);

  const handleToggle = () => {
    if (enabled) {
      lastDiscountRef.current = { type, value: amount };
      setEnabled(false);
      setErrorKey(null);
      onChange(null);
      return;
    }
    const restored = lastDiscountRef.current ?? { type: 'percentage', value: '0' };
    setEnabled(true);
    setType(restored.type);
    setAmount(restored.value);
    if (isPositiveAmount(restored.value)) {
      onChange(restored);
    }
  };

  const handleAmountChange = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    setAmount(raw);
    const validationErrorKey = getDiscountValidationErrorKey(raw, type, subtotal);
    setErrorKey(validationErrorKey);
    if (validationErrorKey) return;
    if (isPositiveAmount(raw)) {
      onChange({ type, value: raw });
    } else if (discount !== null) {
      onChange(null);
    }
  };

  const handleTypeChange = (_event: MouseEvent<HTMLElement>, newType: DiscountType) => {
    if (newType === type) return;
    setType(newType);
    const validationErrorKey = getDiscountValidationErrorKey(amount, newType, subtotal);
    setErrorKey(validationErrorKey);
    if (validationErrorKey) return;
    if (isPositiveAmount(amount)) {
      onChange({ type: newType, value: amount });
    } else if (discount !== null) {
      onChange(null);
    }
  };

  return (
    <PanelContainerStyled data-testid={discountPanelTestIds.panel}>
      <SubtotalSection
        subtotal={subtotal}
        currencySymbol={currencySymbol}
        isEditable={isSubtotalEditable}
        onSubtotalChange={onSubtotalChange}
      />
      {showBillingFrequency && onBillingFrequencyChange && (
        <BillingFrequencySection
          value={billingFrequency ?? BILLING_FREQUENCIES[0]}
          enabledFrequencies={enabledBillingFrequencies}
          onChange={onBillingFrequencyChange}
        />
      )}
      <DiscountSection
        enabled={enabled}
        type={type}
        amount={amount}
        errorKey={errorKey}
        currencySymbol={currencySymbol}
        onToggle={handleToggle}
        onAmountChange={handleAmountChange}
        onTypeChange={handleTypeChange}
        inputRef={amountInputRef}
      />
    </PanelContainerStyled>
  );
};
